// shared/components/ui/breadcrumb/components/breadcrumb/breadcrumb.component.ts

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { BreadcrumbAction, BreadcrumbItem } from '../../models/breadcrumb.model';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="breadcrumb-container" *ngIf="breadcrumbs.length > 1 || actions.length > 0">
      <div class="breadcrumb-left">
        <!-- Back Button -->
        <button
          *ngIf="showBackButton && breadcrumbs.length > 1"
          type="button"
          class="btn-back"
          (click)="onBackClick()"
          aria-label="Go back"
        >
          <i class="bi bi-arrow-left"></i>
        </button>

        <!-- Breadcrumb Navigation -->
        <nav *ngIf="breadcrumbs.length > 0" aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li
              class="breadcrumb-item"
              *ngFor="let item of breadcrumbs; let last = last; let first = first"
              [class.active]="last"
            >
              <ng-container *ngIf="!last && item.url; else lastItem">
                <a [routerLink]="item.url">
                  <i *ngIf="item.icon && first" [class]="item.icon + ' me-1'"></i>
                  {{ item.label }}
                </a>
              </ng-container>
              <ng-template #lastItem>
                <span class="current">
                  <i *ngIf="item.icon && first" [class]="item.icon + ' me-1'"></i>
                  {{ item.label }}
                </span>
              </ng-template>
            </li>
          </ol>
        </nav>
      </div>

      <!-- Action Buttons -->
      <div class="breadcrumb-actions" *ngIf="actions.length > 0">
        <button
          *ngFor="let action of actions"
          type="button"
          [class]="action.class || 'btn btn-primary'"
          [disabled]="action.disabled"
          [title]="action.tooltip || ''"
          (click)="action.action()"
        >
          <i *ngIf="action.icon" [class]="action.icon"></i>
          <span *ngIf="action.label">{{ action.label }}</span>
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .breadcrumb-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 0 6px 10px;
        min-height: 36px;
      }

      .breadcrumb-left {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .btn-back {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        background: #fff;
        color: #333;
        cursor: pointer;
        transition: all 0.2s ease;
        flex-shrink: 0;

        &:hover {
          background: #f5f5f5;
          border-color: #ccc;
        }

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
        }

        i {
          font-size: 16px;
        }
      }

      .breadcrumb {
        display: flex;
        flex-wrap: wrap;
        padding: 0;
        margin: 0;
        list-style: none;
        background: transparent;
        align-items: center;

        .breadcrumb-item {
          display: flex;
          align-items: center;
          font-size: 13px;

          + .breadcrumb-item::before {
            content: '/';
            padding: 0 10px;
            color: #9ca3af;
            font-weight: 400;
          }

          a {
            color: #6366f1;
            text-decoration: none;
            transition: color 0.2s;
            display: flex;
            align-items: center;

            &:hover {
              color: #4f46e5;
              text-decoration: underline;
            }

            i {
              font-size: 14px;
            }
          }

          &.active .current {
            color: #1f2937;
            font-weight: 500;
            display: flex;
            align-items: center;

            i {
              font-size: 14px;
            }
          }
        }
      }

      .breadcrumb-actions {
        display: flex;
        gap: 8px;

        button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          i {
            font-size: 14px;
          }
        }

        .btn-primary {
          background: #6366f1;
          color: #fff;

          &:hover:not(:disabled) {
            background: #4f46e5;
          }
        }

        .btn-secondary {
          background: #fff;
          color: #333;
          border: 1px solid #e0e0e0;

          &:hover:not(:disabled) {
            background: #f5f5f5;
          }
        }

        .btn-danger {
          background: #ef4444;
          color: #fff;

          &:hover:not(:disabled) {
            background: #dc2626;
          }
        }

        .btn-success {
          background: #10b981;
          color: #fff;

          &:hover:not(:disabled) {
            background: #059669;
          }
        }
      }

      @media (max-width: 768px) {
        .breadcrumb-container {
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }

        .breadcrumb-actions {
          width: 100%;
          justify-content: flex-end;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() set customBreadcrumbs(value: BreadcrumbItem[] | undefined) {
    if (value) {
      this._useCustomBreadcrumbs = true;
      this.breadcrumbs = value;
      this.cdr.markForCheck();
    }
  }

  @Input() set showBackButton(value: boolean) {
    this._showBackButtonInput = value;
    this._hasBackButtonInput = true;
  }
  get showBackButton(): boolean {
    return this._hasBackButtonInput ? this._showBackButtonInput : this._showBackButtonFromService;
  }

  @Output() backClick = new EventEmitter<void>();

  breadcrumbs: BreadcrumbItem[] = [];
  actions: BreadcrumbAction[] = [];

  private _useCustomBreadcrumbs = false;
  private _showBackButtonInput = true;
  private _showBackButtonFromService = true;
  private _hasBackButtonInput = false;
  private _backUrl?: string;

  ngOnInit(): void {
    combineLatest([
      this.breadcrumbService.breadcrumbs$,
      this.breadcrumbService.actions$,
      this.breadcrumbService.showBackButton$,
      this.breadcrumbService.backUrl$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([breadcrumbs, actions, showBackButton, backUrl]) => {
        if (!this._useCustomBreadcrumbs) {
          this.breadcrumbs = breadcrumbs;
        }

        this.actions = actions;
        this._showBackButtonFromService = showBackButton;
        this._backUrl = backUrl;

        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBackClick(): void {
    if (this.backClick.observed) {
      this.backClick.emit();
    } else {
      this.breadcrumbService.goBack(this._backUrl);
    }
  }
}
